import { Box, Flex, Icon, Switch, Text, useDisclosure, VStack } from '@chakra-ui/react'
import { Fragment, useEffect, useState } from 'react'
import Loading from '../../components/shared-components/Loading'
import { Prescription } from '../../types'
import { CeasePrescriptionModal } from './components/CeasePrescriptionModal'
import { ReIssuePrescriptionModal } from './components/ReIssuePrescriptionModal'
import { useParams, useSearchParams } from 'react-router-dom'
import CancelPrescriptionModal from './components/CancelPrescriptionModal'
import { useGetAllPrescriptionsQuery } from '../../services/prescriptions'
import RCPagination from '../../components/RCPagination'
import SearchInput from '../../components/form/SearchInput'
import PrescriptionTable from './components/PrescriptionTable'
import { useAppSelector } from '../../app/typedReduxHooks'
import { RootState } from '../../app/store'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import { PrescriberSelector } from '../../components/PrescriberSelector'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import { Permission } from '../../types/permission'
import { useFeatureToggle } from '../../common/utils/feature-toggle/useFeatureToggle'
import { FiUsers } from 'react-icons/fi'

const PrescriptionsPage = () => {
  const { patientId, prescriberId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [showOnlyMyPrescriptions, setShowOnlyMyPrescriptions] = useState(false)

  const isCurrentUserPrescriber = useAppSelector(
    ({ prescriber }: RootState) => !prescriber?.prescriber
  )

  const currentUserPrescriberProfile = useAppSelector(
    ({ prescriber }: RootState) => prescriber?.prescriber
  )
  const { isFeatureEnabled: isSharedPatientsFeatureOn } = useFeatureToggle('SHARED_PATIENTS')

  const users = useAppSelector((state: RootState) => state.users?.data || [])
  const prescribers = users.filter((user) => user.prescriber)

  const [selectedPrescriber, setSelectedPrescriber] = useState<LimitedPrescriberProfile | null>(
    null
  )

  useEffect(() => {
    const urlSearchTerm = searchParams.get('search')
    if (urlSearchTerm) {
      setSearchTerm(urlSearchTerm)
    }
  }, [searchParams])

  useEffect(() => {
    if (prescribers.length > 0) {
      if (prescriberId) {
        const prescriberFromUrl = prescribers.find(
          (u) => u.prescriber?.id === prescriberId
        )?.prescriber
        if (prescriberFromUrl) {
          setSelectedPrescriber(prescriberFromUrl)
          return
        }
      }

      if (!selectedPrescriber) {
        setSelectedPrescriber(
          (currentUserPrescriberProfile
            ? (currentUserPrescriberProfile as LimitedPrescriberProfile)
            : prescribers.find((u) => u.prescriber?.active)?.prescriber) ?? null
        )
      }
    }
  }, [prescribers, prescriberId, currentUserPrescriberProfile, selectedPrescriber])
  const permissions = useAppSelector((state) => state.authentication.permissions)
  const isPrescriptionAdmin = permissions?.includes(Permission.PRESCRIPTION_MANAGEMENT)
  const isDeactivatedPrescriber = !selectedPrescriber?.active

  const [reIssueModalIsOpen, setReIssueModalIsOpen] = useState<boolean>(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false)
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | undefined>(
    undefined
  )
  const [selectedScid, setSelectedScid] = useState<string>('')
  const { isOpen: isCeaseOpen, onOpen: onCeaseOpen, onClose: onCeaseClose } = useDisclosure()

  const currentUserPrescriberId = currentUserPrescriberProfile?.id
  const selectedPrescriberId = selectedPrescriber?.id

  const getEffectivePrescriberId = (): string | undefined => {
    if (!isSharedPatientsFeatureOn) return selectedPrescriberId ?? currentUserPrescriberId ?? ''

    if (showOnlyMyPrescriptions) return currentUserPrescriberId
    return ''
  }

  const { isLoading, data: prescriptionsResponse } = useGetAllPrescriptionsQuery({
    patientId,
    page: String(page),
    limit: '10',
    search: searchTerm,
    prescriberId: getEffectivePrescriberId(),
  })
  const { data: prescriptions, meta: prescriptionsPaginationData } = prescriptionsResponse || {}

  const openCeaseModal = (index: number) => {
    const scid = prescriptions?.[index]?.scid
    if (scid) {
      setSelectedScid(scid)
      onCeaseOpen()
    }
  }

  const openCancelModal = (index: number) => {
    const prescription = prescriptions?.[index]
    prescription && setSelectedPrescription(prescription)
    setIsCancelModalOpen(true)
  }

  const openReIssueModal = (index: number) => {
    const prescription = prescriptions?.[index]
    prescription && setSelectedPrescription(prescription)
    setReIssueModalIsOpen(true)
  }

  const handleSearch = (value: string) => {
    const formattedValue = value.trim()
    setSearchTerm(formattedValue)
    setPage(1)

    // Update URL with search parameter
    if (formattedValue) {
      setSearchParams({ search: formattedValue })
    } else {
      setSearchParams({})
    }
  }

  if (!isPrescriptionAdmin && !currentUserPrescriberProfile) {
    return <UnauthorizedPage />
  }

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" p="12px">
      {isLoading && <Loading />}
      <VStack spacing={4} align="stretch" height="100%" width="100%" overflow="hidden">
        <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }} justifyContent="space-between">
          <Text as="h1" fontSize="3xl" fontWeight="bold">
            Prescriptions
          </Text>{' '}
          {isPrescriptionAdmin && !prescriberId && !isSharedPatientsFeatureOn && (
            <Flex pr={10} gap={4} flexDirection={['column', 'row']} zIndex={2}>
              <Text as={'b'} fontSize={'xl'}>
                Selected prescriber:
              </Text>
              <PrescriberSelector
                users={prescribers}
                selectedPrescriberId={selectedPrescriber?.id}
                onSelect={(id) =>
                  setSelectedPrescriber(
                    prescribers.find((u) => u.prescriber?.id === id)?.prescriber ?? null
                  )
                }
              />
            </Flex>
          )}
        </Flex>
        <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }} justifyContent="space-between">
          <SearchInput
            w={['100%', '100%', '30%']}
            alignSelf="flex-start"
            onSearch={handleSearch}
            placeholder="by patient name or scid or prescribed item name"
            initialValue={searchTerm}
          />

          {!isCurrentUserPrescriber && isSharedPatientsFeatureOn && (
            <Flex
              align="center"
              gap="2"
              bg="white"
              px="3"
              py="2.5"
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              width={{ base: 'full', md: 'fit-content' }}
              flexShrink="0"
            >
              <Icon as={FiUsers} color="#2563eb" />
              <Text fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap" flex={1}>
                Show only my prescriptions
              </Text>
              <Switch
                checked={showOnlyMyPrescriptions}
                onChange={(e) => setShowOnlyMyPrescriptions(e.target.checked)}
                size="sm"
                colorScheme="blue"
              />
            </Flex>
          )}
        </Flex>

        {prescribers.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
            width="100%"
            borderWidth="1px"
            borderRadius="lg"
            p={4}
          >
            <Text fontSize="lg" color="gray.500">
              No prescribers available in this organisation.
            </Text>
          </Box>
        ) : prescriptions ? (
          <Fragment>
            <PrescriptionTable
              prescriptions={prescriptions}
              openCeaseModal={openCeaseModal}
              openReIssueModal={openReIssueModal}
              openCancelModal={openCancelModal}
              isDeactivatedPrescriber={isDeactivatedPrescriber}
              isReadonlyPermission={
                !(
                  currentUserPrescriberProfile?.active &&
                  selectedPrescriber?.id === currentUserPrescriberProfile?.id
                )
              }
              isSharedPatientsFeatureOn={isSharedPatientsFeatureOn}
            />

            {prescriptions?.length > 0 && (
              <Box pt={2}>
                <RCPagination
                  showLessItems
                  current={page}
                  total={prescriptionsPaginationData?.totalItems}
                  pageSize={10}
                  onChange={(currentPage) => setPage(currentPage)}
                />
              </Box>
            )}
          </Fragment>
        ) : null}
      </VStack>

      <CeasePrescriptionModal isOpen={isCeaseOpen} onClose={onCeaseClose} scid={selectedScid} />

      {selectedPrescription && (
        <>
          <CancelPrescriptionModal
            isOpen={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            prescription={selectedPrescription}
          />

          <ReIssuePrescriptionModal
            isOpen={reIssueModalIsOpen}
            onClose={() => {
              setReIssueModalIsOpen(false)
              setSelectedPrescription(undefined)
            }}
            prescriptions={[selectedPrescription]}
          />
        </>
      )}
    </Box>
  )
}

export default PrescriptionsPage
